/**
 * The seven-day money back: whether it can still be had, and the asking for it.
 *
 * One endpoint, `/api/v1/billing/refund/`, and two verbs on it. No id on
 * either: there is one refundable payment per workspace and the bearer token
 * says whose.
 *
 * ## Why the whole panel comes from one `GET`
 *
 * Because every part of it is a claim about money, and the browser was making
 * four of them on its own. It read the invoice list to decide this was a first
 * purchase, counted seven days against the clock in the laptop, quoted the
 * newest invoice row as the payment going back, and offered a list of reasons
 * compiled into the bundle. Each could disagree with the server, and where they
 * did, the one the reader saw was the one that did not count.
 *
 * So `eligible` is the decision, `window_ends_at` is when it lapses, `invoice`
 * is the payment, `reason_options` is the dropdown, and `request` is what
 * became of one already made. The screen draws the answer rather than working
 * one out. See `endpoints.billing.refund`.
 *
 * Money crosses the wire as a decimal string — `"708.00"` — because that is how
 * the backend keeps it. It is a number everywhere on this side.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import { ApiError } from "../../../lib/http";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  RefundOffer,
  RefundOfferResponse,
  RefundPayment,
  RefundInvoiceResponse,
  RefundReason,
  RefundReasonResponse,
  RefundRequestRecord,
  RefundRequestResponse,
  RefundStatus,
  RequestRefundRequest,
  RequestedRefundResponse,
} from "../types/subscriptions.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/** Decimal string to number, defended: an absent or unparseable amount lands on
 *  0 rather than painting "₹ NaN" above the button that gives it back. */
const toAmount = (raw: string | null | undefined): number => {
  const value = Number.parseFloat(raw ?? "");
  return Number.isFinite(value) ? value : 0;
};

/**
 * Where a refund has got to.
 *
 * `PROCESSED` and nothing else means the money has gone; everything the backend
 * can send before that — `REQUESTED` today, whatever it grows into — reads as
 * still on its way. That way round deliberately: "sent back" is the claim
 * somebody stops watching for, and it is not one to make out of a string nobody
 * understood.
 */
const toRefundStatus = (raw: string): RefundStatus =>
  raw?.toUpperCase() === "PROCESSED" ? "PROCESSED" : "PENDING";

/** The payment that would go back, where the offer names one. */
const toPayment = (
  invoice: RefundInvoiceResponse | null | undefined
): RefundPayment | null =>
  invoice
    ? {
        invoiceId: invoice.public_id,
        invoiceNumber: invoice.invoice_number ?? "",
        paidOn: invoice.payment_date ?? "",
        amount: toAmount(invoice.amount),
        currency: invoice.currency ?? "INR",
      }
    : null;

/**
 * The dropdown, in the order the server listed them.
 *
 * Not sorted and not filtered. The order is the backend's editorial choice —
 * "Something else" last, because whoever picks it has already said all they mean
 * to — and a row with no `code` is dropped rather than offered, since picking it
 * would send nothing.
 */
const toReasons = (
  options: RefundReasonResponse[] | null | undefined
): RefundReason[] =>
  (options ?? [])
    .filter((option) => !!option?.code)
    .map((option) => ({ code: option.code, label: option.label || option.code }));

/** One refund request, as the banner, the ended-plan card and the receipt all
 *  read it. */
export const toRefundRequest = (
  request: RefundRequestResponse | null | undefined
): RefundRequestRecord | null =>
  request
    ? {
        id: request.public_id,
        status: toRefundStatus(request.status),
        amount: toAmount(request.amount),
        currency: request.currency ?? "INR",
        reasonCode: request.reason_code ?? "",
        requestedAt: request.requested_at ?? "",
        processedOn: request.processed_at ?? null,
        reference: request.reference ?? "",
        invoiceNumber: request.invoice_number ?? "",
      }
    : null;

/**
 * The offer, closed and empty.
 *
 * What a workspace with nothing to refund is answered with, and what a 404
 * becomes — see `fetchRefundOffer`. Every field is the "there is nothing here"
 * value rather than a missing one, so no screen has to hold a partial offer.
 */
export const NO_REFUND_OFFER: RefundOffer = {
  eligible: false,
  reason: "not_applicable",
  payment: null,
  windowEndsAt: null,
  reasons: [],
  request: null,
};

/** One answer, as the Plan details screen reads it. */
export const toRefundOffer = (response: RefundOfferResponse): RefundOffer => ({
  // `=== true` rather than truthy: this is the decision about whether to offer
  // somebody their money back, and a string or a 1 arriving where a boolean was
  // promised should read as "no" rather than quietly as "yes".
  eligible: response.eligible === true,
  reason: response.reason ?? "",
  payment: toPayment(response.invoice),
  windowEndsAt: response.window_ends_at ?? null,
  reasons: toReasons(response.reason_options),
  request: toRefundRequest(response.request),
});

/**
 * The whole refund panel, in one call.
 *
 * A 404 is the closed offer rather than a failure. It is what a workspace that
 * has never subscribed gets, which is the ordinary state of most of them, and
 * the Plan details screen this feeds is not drawn for one at all — reporting a
 * network error there would be apologising for nothing having happened.
 *
 * Anything else is thrown as it came. A refusal is the server's to word, and a
 * refund panel that cannot be loaded must not be drawn as a closed one: the
 * difference between "you cannot ask" and "we could not find out" is the whole
 * of what the reader needs.
 */
export async function fetchRefundOffer(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<RefundOffer> {
  try {
    const response = await api.get<RefundOfferResponse | null>(
      API_ENDPOINTS.billing.refund,
      { token: await token(getToken, "load your refund options"), signal }
    );

    return response ? toRefundOffer(response) : NO_REFUND_OFFER;
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) {
      return NO_REFUND_OFFER;
    }
    throw cause;
  }
}

/**
 * Asks for the money back.
 *
 * A refund ends the plan rather than stopping it renewing, which is what makes
 * this a different call from `cancelSubscription` and not a flag on it: one
 * gives back a payment and takes the product away with it, the other leaves
 * somebody with what they have paid for until the day it runs out.
 *
 * Which payment goes back is not sent, because there is exactly one it could be
 * and the server already knows it. Nor is the figure: an amount travelling up
 * from a browser is an amount somebody could edit.
 *
 * The reason travels only where one was picked. The card outside promises no
 * questions asked and Confirm never waits on the dropdown, so a request with
 * nothing chosen goes up as `{}` rather than carrying a code nobody selected —
 * putting "Something else" on the record on somebody's behalf would be this app
 * answering a question it was told it could leave unanswered.
 *
 * The window is checked here as well as drawn from `fetchRefundOffer`. Whether
 * the refund is owed is the backend's alone; a request that arrives after the
 * offer has shut comes back as an `ApiError` in the server's own words.
 */
export async function requestRefund(
  getToken: GetToken,
  /** One of the `code`s the offer listed, or `null` where they did not say. */
  reasonCode?: string | null,
  signal?: AbortSignal
): Promise<RefundRequestRecord | null> {
  const body: RequestRefundRequest = reasonCode
    ? { reason_code: reasonCode }
    : {};

  const response = await api.post<RequestedRefundResponse>(
    API_ENDPOINTS.billing.refund,
    body,
    { token: await token(getToken, "request your refund"), signal }
  );

  return toRefundRequest(response?.request);
}
